import React from "react";
import {
	Button,
	ClassNameProps,
	Column,
	Heading,
	If,
	makeClassName,
	Modal,
	ModalProps,
	Row,
	StyleProps,
	TruncatedText,
} from "@hex-insights/core";
import { EditMode } from "@hex-insights/forms";
import { Icon } from "../../../../Components/Icon";
import { MedicalCondition, Student, StudentMedicalCondition, StudentNameFragment } from "../../../../Utilities";
import { StudentMedicalConditionDeleteButton } from "../../../DeleteButtons";
import { MedicalConditionFormat } from "../../../Format";
import { StudentMedicalConditionStudentForm } from "../../../Forms";
import {
	StudentMedicalConditionDeletePermissionRequired,
	StudentMedicalConditionUpdatePermissionRequired,
} from "../PermissionRequired";
import styles from "./styles.module.css";

export type CardStudentMedicalCondition = Pick<StudentMedicalCondition, "id" | "isProminent" | "notes"> & {
	medicalCondition: Pick<MedicalCondition, "id" | "name" | "conditionType">;
};

export type EditableCardStudentMedicalCondition = CardStudentMedicalCondition & {
	student: Pick<Student, "id"> & StudentNameFragment;
};

export type StudentMedicalConditionCardProps = (
	| {
			studentMedicalCondition: CardStudentMedicalCondition;
			editable?: false;
	  }
	| {
			studentMedicalCondition: EditableCardStudentMedicalCondition;
			editable: true;
	  }
) & {
	noLabel?: boolean;
} & Partial<ClassNameProps & StyleProps>;

export function StudentMedicalConditionCard({
	studentMedicalCondition,
	editable,
	noLabel = false,
	className,
	style,
}: StudentMedicalConditionCardProps) {
	const { isModalOpen: isEditModalOpen, openModal: openEditModal, closeModal: closeEditModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Column
				justify="spaced-start"
				verticalSpacing="0.375rem"
				className={makeClassName(styles["card"], className)}
				style={style}
			>
				<If condition={!noLabel}>
					<Row justify="spaced-start" align="center">
						<TruncatedText className={styles["card__label"]}>
							{MedicalConditionFormat.Fields.conditionType(studentMedicalCondition.medicalCondition.conditionType)}
						</TruncatedText>

						<If condition={studentMedicalCondition.isProminent}>
							<span title="Primary">
								<Icon.Star color="var(--theme-colors__pink)" size={16} weight="fill" className="block" />
							</span>
						</If>
					</Row>
				</If>

				<Column justify="spaced-start" verticalSpacing="0.125rem">
					<Row justify="space-between" align="center">
						<TruncatedText className={styles["card__name"]}>
							{MedicalConditionFormat.name(studentMedicalCondition.medicalCondition)}
						</TruncatedText>

						<Row justify="spaced-start">
							<If condition={editable ?? false}>
								<StudentMedicalConditionUpdatePermissionRequired>
									<Button onClick={openEditModal} style={{ padding: 0, border: "none", cursor: "pointer" }}>
										<Icon.PencilSimpleLine color="var(--primary-color)" size={16} className="block" />
									</Button>
								</StudentMedicalConditionUpdatePermissionRequired>
							</If>
						</Row>
					</Row>

					<If condition={!!studentMedicalCondition.notes}>
						<TruncatedText className={styles["card__description"]}>{studentMedicalCondition.notes}</TruncatedText>
					</If>
				</Column>
			</Column>

			{editable && (
				<Modal.If condition={isEditModalOpen}>
					<EditModal studentMedicalCondition={studentMedicalCondition} onClose={closeEditModal} />
				</Modal.If>
			)}
		</React.Fragment>
	);
}

const editFormStateArgs = { editMode: EditMode.WriteOnly };

type EditModalProps = {
	studentMedicalCondition: EditableCardStudentMedicalCondition;
} & Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

function EditModal({ studentMedicalCondition, ifRef, onClose, className, style }: EditModalProps) {
	const onSuccess = React.useCallback(() => {
		onClose?.();
	}, [onClose]);

	return (
		<Modal ifRef={ifRef} onClose={onClose} className={makeClassName(styles["edit-modal"], className)} style={style}>
			<Modal.Header>
				<Heading.H2 noMargin>Edit Medical Condition</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<Column justify="spaced-start">
					<StudentMedicalConditionStudentForm.ControlledDetail
						studentMedicalCondition={studentMedicalCondition}
						formStateArgs={editFormStateArgs}
						noChangesDialog
						onSuccess={onSuccess}
					/>

					<StudentMedicalConditionDeletePermissionRequired>
						<StudentMedicalConditionDeleteButton
							instanceID={studentMedicalCondition.id}
							size="small"
							onSuccess={onSuccess}
						/>
					</StudentMedicalConditionDeletePermissionRequired>
				</Column>
			</Modal.Body>
		</Modal>
	);
}
